import { type NotificationCatalogueKey } from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/**
 * The **cross-module read bridge** for the notification catalogue (W6).
 *
 * It exists for one caller today and will have several: a violation type names
 * a `legalNoticeTemplateKey`, a campaign will name a template, and a future
 * course or billing notice will do the same. Every one of them sits in a
 * different module, and W6 forbids each of them importing
 * `modules/Notifications/services/notifications.api` — which is precisely the
 * coupling that a picker "just reading the other module's hook" would create.
 *
 * **Read-only, and a whole-catalogue read rather than a `ServerSelect` page.**
 * W6 sends backend option pickers to `ServerSelect` because a paginated
 * *resource* must never be preloaded — but this route answers the **code**
 * catalogue: a fixed list declared in `libs/contracts`, roughly a dozen entries,
 * with no page and no search on the wire. Paging it would be inventing a
 * boundary the api does not have.
 *
 * The `authoredChannels` on each entry are the one part that IS server state,
 * which is why this is a request at all rather than an import of
 * `NOTIFICATION_CATALOGUE`: whether anybody has written the copy is a fact about
 * the deployment, and a picker that cannot say "nobody has written this yet" is
 * the picker P20b was missing.
 */
export const notificationTemplateLookupApi = createApi({
  reducerPath: 'notificationTemplateLookupApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['NotificationCatalogue'],
  endpoints: (builder) => ({
    lookupNotificationCatalogue: builder.query<NotificationCatalogueKey[], void>({
      query: () => ({ url: '/admin/notification-templates/catalogue', method: 'GET' }),
      providesTags: ['NotificationCatalogue'],
    }),
  }),
});

export const { useLookupNotificationCatalogueQuery } = notificationTemplateLookupApi;
